import {classToPlain, Exclude, Expose, plainToClass} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON} from "../web/validation";
import {CargoSubtype, toCargoSubtype} from "./cargo-subtype";
import {ContainerEntity} from "./container";
import {RegionEntity} from "./region";
import {adjustToTimeZone} from "../date/date";
import {DatabaseCode, toDatabaseCode} from "./database-code";
import {convertLinearQuantity} from "../util/util";

/** Validator for a container level history type value. */
export const ContainerLevelHistoryValidator = t.intersection([
  t.type({
    id: t.string,
    containerId: t.string,
    regionId: t.string,
    type: t.string,
    cargoSubtype: t.string,
    createDttm: t.number,
  }),
  t.partial({
    containerName: t.string,
    pipelineMeterId: t.string,
    reportedById: t.string,
    createById: t.string,
    level: t.number,
    levelUom: t.string,
    volume: t.number,
    volumeUom: t.string,
  }),
]);

/** Validator for an array of container level history type values. */
export const ContainerLevelHistoryArrayValidator = t.array(
  ContainerLevelHistoryValidator
);

export type ContainerLevelHistory = t.TypeOf<
  typeof ContainerLevelHistoryValidator
>;
export type ContainerLevelHistoryArray = t.TypeOf<
  typeof ContainerLevelHistoryArrayValidator
>;

export interface ContainerLevelHistoryType extends DatabaseCode {
}

const driverEntered: ContainerLevelHistoryType = {
  code: 'Driver Entered',
  displayValue: {
    id: 'ots.ui.entity.containerlevelhistory.type.driver',
    defaultMessage: 'Driver Entered',
  },
};

const fieldEntered: ContainerLevelHistoryType = {
  code: 'Field Entered',
  displayValue: {
    id: 'ots.ui.entity.containerlevelhistory.type.field',
    defaultMessage: 'Field Entered',
  },
};

const meter: ContainerLevelHistoryType = {
  code: 'Meter',
  displayValue: {
    id: 'ots.ui.entity.containerlevelhistory.type.meter',
    defaultMessage: 'Meter',
  },
};

export const containerLevelHistoryTypes = {
  driverEntered,
  fieldEntered,
  meter,
};

export const toContainerLevelHistoryType = (code: string): ContainerLevelHistoryType => toDatabaseCode(code, Object.values(containerLevelHistoryTypes))

export type LevelType = 'volume' | 'height';

export const isLevelType = (value: string): value is LevelType => {
  return value === "volume" || value === "height";
};

/**
 * A container level history.
 */
@Exclude()
export class ContainerLevelHistoryEntity {
  @Expose() id!: string;
  @Expose() containerId!: string;
  @Expose() containerName?: string;
  @Expose() pipelineMeterId?: string;
  @Expose() regionId!: string;
  @Expose({name: 'type'}) private _type!: string;
  @Expose({name: 'level'}) height?: number;
  @Expose({name: 'levelUom'}) heightUom?: string;
  @Expose() volume?: number;
  @Expose() volumeUom?: string;
  @Expose({name: 'cargoSubtype'}) private _cargoSubtype!: string;
  @Expose() reportedById?: string;
  @Expose({name: 'createById'}) createdById?: string;
  @Expose() createDttm!: number;

  /**
   * Constructor for ContainerLevelHistoryEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a ContainerLevelHistoryEntity.
   *
   * @param name The name of the container.
   * @param id (optional) The primary key of the container.
   */
  static create(
    container: ContainerEntity,
    region: RegionEntity,
    type: ContainerLevelHistoryType,
    height: number,
    heightUom: string,
    volume: number,
    volumeUom: string,
    cargoSubtype: CargoSubtype,
    id: string | undefined = uuid()
  ): ContainerLevelHistoryEntity {
    const entity = new ContainerLevelHistoryEntity();
    entity.id = id;
    entity.containerId = container.id;
    entity.regionId = region.id;
    entity.type = type;
    entity.height = height;
    entity.heightUom = heightUom;
    entity.volume = volume;
    entity.volumeUom = volumeUom;
    entity.cargoSubtype = cargoSubtype;
    entity.createDttm = adjustToTimeZone(new Date()).getTime();
    return entity;
  }

  /**
   * Create a ContainerLevelHistoryEntity.
   *
   * @param name The name of the container.
   * @param id (optional) The primary key of the container.
   */
  static createHeight(
    container: ContainerEntity,
    region: RegionEntity,
    type: ContainerLevelHistoryType,
    height: number,
    heightUom: string,
    cargoSubtype: CargoSubtype,
    id: string | undefined = uuid()
  ): ContainerLevelHistoryEntity {
    const entity = new ContainerLevelHistoryEntity();
    entity.id = id;
    entity.containerId = container.id;
    entity.regionId = region.id;
    entity.type = type;
    entity.height = height;
    entity.heightUom = heightUom;
    entity.cargoSubtype = cargoSubtype;
    entity.createDttm = adjustToTimeZone(new Date()).getTime();
    return entity;
  }

  /**
   * Create a ContainerLevelHistoryEntity.
   *
   * @param name The name of the container.
   * @param id (optional) The primary key of the container.
   */
  static createVolume(
    container: ContainerEntity,
    region: RegionEntity,
    type: ContainerLevelHistoryType,
    volume: number,
    volumeUom: string,
    cargoSubtype: CargoSubtype,
    id: string | undefined = uuid()
  ): ContainerLevelHistoryEntity {
    const entity = new ContainerLevelHistoryEntity();
    entity.id = id;
    entity.containerId = container.id;
    entity.regionId = region.id;
    entity.type = type;
    entity.volume = volume;
    entity.volumeUom = volumeUom;
    entity.cargoSubtype = cargoSubtype;
    entity.createDttm = adjustToTimeZone(new Date()).getTime();
    return entity;
  }

  /**
   * Create a ContainerLevelHistoryEntity from a ContainerLevelHistory type value.
   *
   * @param containerLevel The container level type value.
   */
  static from(
    containerLevel: ContainerLevelHistory
  ): ContainerLevelHistoryEntity {
    const entity: ContainerLevelHistoryEntity = plainToClass(
      ContainerLevelHistoryEntity,
      containerLevel,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the ContainerLevelHistoryEntity to a ContainerLevelHistory type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<ContainerLevelHistory> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ContainerLevelHistoryValidator, object);
  }

  get type(): ContainerLevelHistoryType {
    /* istanbul ignore next */
    return toContainerLevelHistoryType(this._type);
  }

  set type(type: ContainerLevelHistoryType) {
    /* istanbul ignore next */
    this._type = type.code;
  }

  get cargoSubtype(): CargoSubtype {
    return toCargoSubtype(this._cargoSubtype);
  }

  set cargoSubtype(type: CargoSubtype) {
    this._cargoSubtype = type.code;
  }

  getLevel = (levelType: LevelType, heightUom: string | undefined = undefined): number | undefined => {
    if (levelType === 'height') {
      if (heightUom === undefined) {
        return this.height;
      } else {
        return convertLinearQuantity(this.getLevelUom(levelType), heightUom, this.height)
      }
    } else if (levelType === 'volume') {
      return this.volume;
    }

    return undefined;
  };

  getLevelUom = (levelType: LevelType): string | undefined => {
    if (levelType === 'height') {
      return this.heightUom;
    } else if (levelType === 'volume') {
      return this.volumeUom;
    }

    return undefined;
  };
}

export type ContainerLevelHistorySearchCriteria = {
  fromDttm?: number;
  toDttm?: number;
  containerId?: string;
  regionId?: string;
  type?: string;
  cargoSubtype?: string;
  authentication?: string;
  withTransports?: boolean
};
